<template>
  <section class="ListBuilder">
    <header class="ListBuilder__header">
      <div class="ListBuilder__heading">
        <InputLabel
          v-bind="labelProps"
          class="ListBuilder__label"
          :isRequired="!allowEmpty"
          :text="label"
        />

        <TooltipIcon
          v-if="tooltip"
          :id="tooltipId"
          v-bind="tooltipProps"
          class="ListBuilder__tooltip"
          :text="tooltip"
        />
      </div>

      <ParagraphBody
        v-if="description"
        :id="descriptionId"
        class="ListBuilder__description"
      >
        {{ description }}
      </ParagraphBody>
    </header>

    <!-- https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions -->
    <div
      :id="itemRegionId"
      :aria-describedby="[descriptionId, tooltipId].join(' ')"
      aria-live="polite"
      role="region"
    >
      <div
        v-for="(item, i) in items"
        :key="i"
        class="ListBuilder__item"
      >
        <slot
          v-bind="item"
          name="item"
        />

        <IconButton
          :aria-controls="itemRegionId"
          :ariaLabel="`Remove ${label} item ${i}`"
          class="ListBuilder__remove"
          data-test-id="ListBuilder__remove"
          hasNoBackground
          icon="delete"
          informational
          :isDisabled="!allowEmpty && items.length < 2 && i === 0"
          @click="() => $emit('remove', i)"
        />
      </div>
    </div>

    <IconButton
      :aria-controls="itemRegionId"
      :ariaLabel="`Add new ${label} item`"
      class="ListBuilder__add"
      data-test-id="ListBuilder__add"
      hasOutline
      :icon="buttonIcon"
      matchText
      :text="buttonText"
      type="secondary"
      @click="() => $emit('add')"
    />
  </section>
</template>

<script>
import { v4 as uuid } from 'uuid';

import {
  IconButton,
  InputLabel,
  ParagraphBody,
  TooltipIcon,
} from '@jumpcloud/ui-components';

export default {
  name: 'ListBuilder',

  components: {
    IconButton,
    InputLabel,
    ParagraphBody,
    TooltipIcon,
  },

  props: {
    allowEmpty: {
      type: Boolean,
      default: true,
    },

    buttonIcon: {
      type: String,
      default: 'add',
    },

    buttonText: {
      type: String,
      default: 'Add',
    },

    description: {
      type: String,
      default: '',
    },

    items: {
      type: Array,
      default: () => [],
    },

    label: {
      type: String,
      required: true,
    },

    labelProps: {
      type: Object,
      default: () => ({}),
    },

    tooltip: {
      type: String,
      default: '',
    },

    tooltipProps: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    const id = uuid();

    return {
      descriptionId: this.description ? `list-description-${id}` : '',
      itemRegionId: `list-item-region-${id}`,
      tooltipId: this.tooltip ? `list-tooltip-${id}` : '',
    };
  },
};
</script>

<style scoped>
.ListBuilder {
  margin-bottom: var(--jc-spacer-large);
}

.ListBuilder__header {
  margin-bottom: var(--jc-spacer);
}

.ListBuilder__heading {
  align-items: center;
  display: flex;
}

.ListBuilder__label {
  margin-bottom: 0;
  margin-right: var(--jc-spacer-small);
}

.ListBuilder__tooltip {
  /*
    Remove tooltip offset to fix alignment.
    Why does the tooltip need a bottom offset at all?
  */
  bottom: 0 !important;

  /*
    for some reason tooltip z-index is set to 1
    which allows normal flow content to overlay it when open
  */
  z-index: 2;
}

.ListBuilder__item {
  display: grid;
  grid-column-gap: var(--jc-spacer-small);
  grid-template-columns: 1fr auto;
  grid-template-rows: 1fr;
}

.ListBuilder__remove {
  align-self: center;
}
</style>
